if (!customElements.get('timeline-scroller')) {
  class TimelineScroller extends HTMLElement {
    constructor() {
      super();
      window.initScriptOnDemand(this, this.init.bind(this), 800);
    }

    init() {
      this.viewport = this.querySelector('.timeline__viewport');
      this.stage = this.querySelector('.timeline__stage');
      this.eras = Array.from(this.querySelectorAll('.era'));
      this.buttons = Array.from(this.querySelectorAll('.timeline__rail-button'));
      this.rail = this.querySelector('.timeline__rail');

      if (!this.viewport || this.eras.length === 0) return;

      this.count = this.eras.length;
      this.activeIndex = -1;
      this.frame = null;
      this.observer = null;
      this.flowObserver = null;
      this.listening = false;

      this.pinnedQuery = window.matchMedia('(min-width: 990px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = this.onScroll.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onRailClick = this.onRailClick.bind(this);
      this.onRailKeydown = this.onRailKeydown.bind(this);
      this.update = this.update.bind(this);

      this.rail?.addEventListener('click', this.onRailClick);
      this.rail?.addEventListener('keydown', this.onRailKeydown);
      this.pinnedQuery.addEventListener('change', this.onModeChange);

      this.setMode();

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const index = this.eras.indexOf(event.target.closest('.era'));
          if (index > -1) this.goTo(index, 'auto');
        });
      }
    }

    disconnectedCallback() {
      this.teardownMode();
      this.rail?.removeEventListener('click', this.onRailClick);
      this.rail?.removeEventListener('keydown', this.onRailKeydown);
      this.pinnedQuery?.removeEventListener('change', this.onModeChange);
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    onModeChange() {
      this.teardownMode();
      this.setMode();
    }

    setMode() {
      this.pinned = this.pinnedQuery.matches;

      if (this.pinned) {
        // Only listen while the section is anywhere near the viewport.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.startListening();
              } else {
                this.stopListening();
              }
            });
          },
          { rootMargin: '200px 0px 200px 0px' }
        );
        this.observer.observe(this);
        this.update();
      } else {
        // Flow mode: the era nearest the middle of the viewport owns the rail.
        this.flowObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = this.eras.indexOf(entry.target);
              if (index > -1) this.setActive(index);
            });
          },
          { rootMargin: '-45% 0px -45% 0px' }
        );
        this.eras.forEach((era) => this.flowObserver.observe(era));

        // Reveal anything already on screen, and anything that scrolls in.
        this.revealObserver = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              obs.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -15% 0px' }
        );
        this.eras.forEach((era) => this.revealObserver.observe(era));

        this.setActive(Math.max(this.activeIndex, 0));
      }
    }

    teardownMode() {
      this.stopListening();
      this.observer?.disconnect();
      this.observer = null;
      this.flowObserver?.disconnect();
      this.flowObserver = null;
      this.revealObserver?.disconnect();
      this.revealObserver = null;

      // Pinned mode hides inactive eras; flow mode must not inherit that state.
      this.eras.forEach((era) => era.classList.remove('is-active'));
      this.activeIndex = -1;
    }

    startListening() {
      if (this.listening) return;
      this.listening = true;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    stopListening() {
      if (!this.listening) return;
      this.listening = false;
      window.removeEventListener('scroll', this.onScroll);
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    /**
     * Read the section's scroll position and translate it into
     * an active era, an in-era progress value and a rail fill.
     */
    update() {
      if (!this.pinned) return;

      const metrics = this.getMetrics();
      if (!metrics) return;

      const { progress } = metrics;
      const scaled = progress * this.count;
      const index = Math.min(Math.floor(scaled), this.count - 1);
      const eraProgress = Math.min(Math.max(scaled - index, 0), 1);

      const railProgress =
        this.count > 1 ? Math.min(Math.max((scaled - 0.5) / (this.count - 1), 0), 1) : 1;

      this.style.setProperty('--rail-progress', railProgress.toFixed(4));
      this.eras[index].style.setProperty('--p', eraProgress.toFixed(4));

      if (index !== this.activeIndex) this.setActive(index);
    }

    /**
     * Distance travelled through the sticky track, as 0–1.
     */
    getMetrics() {
      const stageHeight = this.stage.offsetHeight;
      const track = this.viewport.offsetHeight - stageHeight;
      if (track <= 0) return null;

      const stageTop = this.stage.getBoundingClientRect().top;
      const viewportTop = this.viewport.getBoundingClientRect().top;
      // While pinned, stageTop is the sticky offset; before/after it rides along.
      const stickyOffset = parseFloat(getComputedStyle(this.stage).top) || 0;

      const scrolled = stickyOffset - viewportTop;
      const progress = Math.min(Math.max(scrolled / track, 0), 1);

      return { progress, track, stageHeight, stageTop, viewportTop, stickyOffset };
    }

    setActive(index) {
      this.activeIndex = index;

      this.eras.forEach((era, i) => {
        const isActive = i === index;
        era.classList.toggle('is-active', isActive);
        if (this.pinned) era.classList.toggle('is-visible', isActive);
      });

      this.buttons.forEach((button, i) => {
        if (i === index) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      if (!this.pinned && this.count > 1) {
        this.style.setProperty('--rail-progress', (index / (this.count - 1)).toFixed(4));
      }
    }

    onRailClick(event) {
      const button = event.target.closest('.timeline__rail-button');
      if (!button) return;

      const index = this.buttons.indexOf(button);
      if (index > -1) this.goTo(index);
    }

    onRailKeydown(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

      const button = event.target.closest('.timeline__rail-button');
      if (!button) return;

      const current = this.buttons.indexOf(button);
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = current + step;
      if (next < 0 || next >= this.count) return;

      event.preventDefault();
      this.buttons[next].focus();
      this.goTo(next);
    }

    goTo(index, behavior) {
      const reduceMotion = this.motionQuery.matches;
      const scrollBehavior = behavior || (reduceMotion ? 'auto' : 'smooth');

      if (this.pinned) {
        const metrics = this.getMetrics();
        if (!metrics) return;

        // Land in the middle of the era's slice of the track.
        const target =
          window.scrollY +
          metrics.viewportTop -
          metrics.stickyOffset +
          (metrics.track * (index + 0.5)) / this.count;

        window.scrollTo({ top: Math.round(target), behavior: scrollBehavior });
      } else {
        const era = this.eras[index];
        const railHeight = this.rail?.offsetHeight || 0;
        const headerHeight =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height')
          ) || 0;

        const target =
          window.scrollY + era.getBoundingClientRect().top - railHeight - headerHeight - 16;

        window.scrollTo({ top: Math.round(target), behavior: scrollBehavior });
      }
    }
  }

  customElements.define('timeline-scroller', TimelineScroller);
}
